import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import clsx from 'clsx';
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from 'react';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { mergeProps } from 'src/core/utils';

import { Stack } from '../Stack';
import * as styles from './RadioGroup.css';

export const RadioGroup = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixRadioGroup.Root>
>(function Root({ ...props }, ref) {
  return <RadixRadioGroup.Root {...props} ref={ref} />;
});

export const Radio = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof RadixRadioGroup.Item> & {
    label: ReactNode;
    labelSubtext?: ReactNode;
    variant?: 'regular' | 'outline';
    containerClassName?: string;
    rightContent?: ReactNode;
  }
>(function Root(
  {
    label,
    labelSubtext,
    value,
    disabled,
    checked, // if passed, the radio will be controlled by outside. Otherwise, it will be controlled by the RadioGroup
    variant = 'regular',
    containerClassName,
    rightContent,
    ...props
  },
  ref
) {
  const { isDarkMode } = useSiteTheme();

  // Even passing `checked` as undefined will cause the radio to be controlled by outside and make it think it's unselected
  // So only explicitly pass `checked` if it's not undefined (controlled by outside)
  const propsWithChecked = {
    ...props,
  } as ComponentPropsWithoutRef<typeof RadixRadioGroup.Item>;

  if (checked != null) {
    propsWithChecked.checked = checked;
  }

  return (
    <Stack
      id={`radio-${value}`}
      gap="m"
      className={clsx(containerClassName, {
        [styles.radioOutline]: variant === 'outline',
        [styles.disabled]: disabled,
      })}
      alignItems="center"
    >
      <RadixRadioGroup.Item
        ref={ref}
        {...mergeProps(
          {
            className: clsx(styles.radioItem, {
              [styles.radioItemChecked]: checked && !isDarkMode,
              [styles.radioItemCheckedDark]: checked && isDarkMode,
            }),
          },
          propsWithChecked
        )}
        value={value}
        id={value}
      >
        <RadixRadioGroup.Indicator
          className={clsx(styles.radioIndicator, {
            [styles.radioIndicatorDisabled]: disabled,
            [styles.disabled]: disabled,
            [styles.radioIndicatorCheckedDark]: checked && isDarkMode,
          })}
        />
      </RadixRadioGroup.Item>
      <Stack
        gap="m"
        alignItems="center"
        justifyContent="spaceBetween"
        width="full"
      >
        <Stack direction="column">
          <label
            className={clsx(styles.radioLabel, {
              [styles.disabled]: disabled,
            })}
            htmlFor={value}
          >
            {label}
          </label>
          {labelSubtext && (
            <label
              className={clsx(styles.radioSublabel, {
                [styles.disabled]: disabled,
              })}
              htmlFor={value}
            >
              {labelSubtext}
            </label>
          )}
        </Stack>
        {rightContent}
      </Stack>
    </Stack>
  );
});
